import React, { useEffect, useState } from 'react';
import { Plus, Mail, Search, Filter, MailOpen, Trash2, RefreshCw, User, X } from 'lucide-react';
import { Layout } from '../components/Layout/Layout';
import { MessageList } from '../components/Messages/MessageList';
import { MessageComposer } from '../components/Messages/MessageComposer';
import { MessageViewer } from '../components/Messages/MessageViewer';
import { useMessageStore } from '../store/messageStore';
import { useAuthStore } from '../store/authStore';
import type { Message, MessageFilters } from '../types/messaging';

export const MessagesPage: React.FC = () => {
  const { user } = useAuthStore();
  const { 
    messages, 
    unreadCount, 
    isLoading, 
    error,
    fetchMessages, 
    sendMessage, 
    replyToMessage,
    markAsRead,
    markAllAsRead,
    deleteMessage,
    clearError
  } = useMessageStore();

  const [filters, setFilters] = useState<MessageFilters>({});
  const [showComposer, setShowComposer] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [replyTo, setReplyTo] = useState<{
    messageId: string;
    subject: string;
    recipientId: string;
    recipientName: string;
  } | null>(null);

  useEffect(() => {
    fetchMessages(filters);
  }, [filters]);

  const handleMessageClick = async (message: Message) => {
    if (!message.is_read) {
      await markAsRead(message.id);
    }
    setSelectedMessage(message);
    setShowViewer(true);
  };

  const handleReply = (message: Message) => {
    setReplyTo({
      messageId: message.id,
      subject: message.subject,
      recipientId: message.sender_id,
      recipientName: message.sender?.full_name || message.sender?.email || 'Utilisateur'
    });
    setShowComposer(true);
    setShowViewer(false);
  };

  const handleSendMessage = async (messageData: any) => {
    await sendMessage(messageData);
    setShowComposer(false);
    setReplyTo(null);
    // Refresh messages
    await fetchMessages(filters);
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
      await deleteMessage(messageId);
    }
  };

  const handleFilterChange = (key: keyof MessageFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  const hasActiveFilters = Object.values(filters).some(value => 
    value !== undefined && value !== '' && value !== false
  );

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Messagerie</h1>
            <p className="text-gray-600 mt-1">
              Communiquez avec les autres utilisateurs de la plateforme
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <MailOpen className="w-4 h-4" />
                <span>Tout marquer lu ({unreadCount})</span>
              </button>
            )}
            
            <button
              onClick={() => fetchMessages(filters)}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Actualiser</span>
            </button>
            
            <button
              onClick={() => setShowComposer(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Nouveau message</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total messages</p>
                <p className="text-2xl font-bold text-gray-900">{messages.length}</p>
              </div>
              <Mail className="w-8 h-8 text-blue-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Non lus</p>
                <p className="text-2xl font-bold text-red-600">{unreadCount}</p>
              </div>
              <MailOpen className="w-8 h-8 text-red-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Lus</p>
                <p className="text-2xl font-bold text-green-600">{messages.length - unreadCount}</p>
              </div>
              <Mail className="w-8 h-8 text-green-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Conversations</p>
                <p className="text-2xl font-bold text-purple-600">
                  {new Set(messages.map(m => m.sender_id)).size}
                </p>
              </div>
              <User className="w-8 h-8 text-purple-400" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher dans les messages..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Unread Filter */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="unread_only"
                checked={filters.unread_only || false}
                onChange={(e) => handleFilterChange('unread_only', e.target.checked || undefined)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="unread_only" className="text-sm text-gray-700">
                Seulement les non lus
              </label>
            </div>

            {/* Date Range */}
            <input
              type="date"
              value={filters.date_from || ''}
              onChange={(e) => handleFilterChange('date_from', e.target.value || undefined)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Date de début"
            />

            <div className="flex items-center space-x-2">
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Effacer filtres
                </button>
              )}
              <div className="flex items-center space-x-1 text-sm text-gray-600">
                <Filter className="w-4 h-4" />
                <span>{messages.length} message{messages.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <p className="text-red-600">{error}</p>
              <button
                onClick={clearError}
                className="text-red-500 hover:text-red-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Messages List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Messages reçus
              {unreadCount > 0 && (
                <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-sm rounded-full">
                  {unreadCount} non lu{unreadCount > 1 ? 's' : ''}
                </span>
              )}
            </h2>
          </div>
          
          <MessageList
            messages={messages}
            onMessageClick={handleMessageClick}
            onReply={handleReply}
            onDelete={handleDeleteMessage}
            onMarkAsRead={markAsRead}
            isLoading={isLoading}
          />
        </div>

        {/* Message Composer Modal */}
        <MessageComposer
          isOpen={showComposer}
          onClose={() => {
            setShowComposer(false);
            setReplyTo(null);
          }}
          onSend={handleSendMessage}
          replyTo={replyTo || undefined}
          isLoading={isLoading}
        />

        {/* Message Viewer Modal */}
        {showViewer && selectedMessage && (
          <MessageViewer
            message={selectedMessage}
            onClose={() => {
              setShowViewer(false);
              setSelectedMessage(null);
            }}
            onReply={handleReply}
            onDelete={handleDeleteMessage}
          />
        )}
      </div>
    </Layout>
  );
};